import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { CurrentUser, CurrentUserService } from '@rma/dashboard/authentication/public/user';
import { LoginPathsService } from '@rma/dashboard/authentication/public/util-login-paths';
import { map, Observable, take } from 'rxjs';

export type AuthenticationLevels = 'non-consumer' | 'individual' | 'office';

@Injectable({ providedIn: 'root' })
export class AuthRedirectService {
  public constructor(
    private readonly currentUserService: CurrentUserService,
    private readonly location: Location,
    private readonly loginPathsService: LoginPathsService,
  ) {}

  public hasAuthOrRedirect(level: AuthenticationLevels = 'non-consumer'): Observable<boolean> {
    return this.currentUserService.currentUser$.pipe(
      take(1),
      map((user) => this.handleRedirect(level, user)),
    );
  }

  private handleRedirect(level: AuthenticationLevels, user: CurrentUser | null): boolean {
    switch (level) {
      case 'non-consumer':
        return this.handleNonConsumer(user);
      case 'individual':
        return this.handleIndividual(user);
      case 'office':
        return this.handleOffice(user);
      default:
        return false;
    }
  }

  private handleNonConsumer(user: CurrentUser | null) {
    if (!user || user.userType === 'Consumer') {
      this.redirectUnauthenticatedUser();
      return false;
    }
    return true;
  }

  private handleIndividual(user: CurrentUser | null) {
    if (!user) {
      this.redirectUnauthenticatedUser();
      return false;
    } else if (user.agent) {
      return true;
    } else if (user.userType === 'AgencyAdmin') {
      this.redirectToAgencyState();
      return false;
    }

    return true;
  }

  private handleOffice(user: CurrentUser | null) {
    if (!user) {
      this.redirectUnauthenticatedUser();
      return false;
    } else if (user.userType !== 'AgencyAdmin' && user.userType !== 'AgencyAdminAgent') {
      this.redirectToDashboard();
      return false;
    }
    return true;
  }

  private redirectToAgencyState(): void {
    this.redirectToDashboard();
  }

  private redirectUnauthenticatedUser(): void {
    this.loginPathsService.goToClaim();
  }

  private redirectToDashboard() {
    this.location.go('/');
  }
}
